import { useRef, useState } from 'react'
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, Info } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { usePreviewTrainingImport, useCommitTrainingImport } from '@/hooks/use-api'
import { useToast } from '@/hooks/use-toast'
import { explainError } from '@/lib/explain-error'
import type { TrainingImportReport, TrainingImportResult } from '@/types'

/**
 * Importing the "Training Tracking" workbook, for Admins.
 *
 * Two steps, like the mass-upload screen: choose a file and see exactly what would
 * happen, then confirm. Nothing is written by the first step. This is how the data
 * reaches dev and prod at all — there is no other path — so the report is the point of
 * the page, not decoration.
 *
 * Re-importing is safe. Every row is keyed on what identifies it in the sheet, so a
 * second import refreshes rather than duplicates, and an account somebody resolved
 * through the unmatched-names queue is never overwritten by the sheet.
 */

/** How a tab's outcome reads at a glance. */
function TabRow({ tab }: { tab: TrainingImportReport['tabs'][number] }) {
  const blocked = tab.truncated || tab.missing
  return (
    <div className="border-b py-2.5 last:border-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="min-w-[11rem] font-medium">{tab.label}</span>
        {blocked ? (
          <Badge variant="destructive">Skipped</Badge>
        ) : (
          <>
            {tab.toCreate > 0 && <Badge variant="secondary">{tab.toCreate} new</Badge>}
            {tab.toRefresh > 0 && <Badge variant="outline">{tab.toRefresh} updated</Badge>}
            {tab.toRestore > 0 && (
              <Badge variant="destructive">{tab.toRestore} deleted in CRM</Badge>
            )}
            {tab.toCreate === 0 && tab.toRefresh === 0 && tab.toRestore === 0 && (
              <span className="text-sm text-muted-foreground">No changes</span>
            )}
          </>
        )}
        <span className="ml-auto text-xs text-muted-foreground">
          {tab.rowsInSheet} row{tab.rowsInSheet === 1 ? '' : 's'} in the file
        </span>
      </div>

      {tab.notes.map((note) => (
        <p key={note} className="mt-1 flex items-start gap-1.5 text-xs text-muted-foreground">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-warning" aria-hidden="true" />
          {note}
        </p>
      ))}

      {tab.unresolvedNames.length > 0 && (
        <details className="mt-1">
          <summary className="cursor-pointer text-xs text-muted-foreground">
            Show the {tab.unresolvedNames.length} unmatched name
            {tab.unresolvedNames.length === 1 ? '' : 's'}
          </summary>
          <ul className="mt-1 list-inside list-disc text-xs text-muted-foreground">
            {tab.unresolvedNames.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}

export default function TrainingImport() {
  const { toast } = useToast()
  const fileInput = useRef<HTMLInputElement>(null)

  const [file, setFile] = useState<File | null>(null)
  const [report, setReport] = useState<TrainingImportReport | null>(null)
  const [result, setResult] = useState<TrainingImportResult | null>(null)
  const [restoreDeleted, setRestoreDeleted] = useState(false)

  const previewImport = usePreviewTrainingImport()
  const commitImport = useCommitTrainingImport()

  const reset = () => {
    setFile(null)
    setReport(null)
    setResult(null)
    setRestoreDeleted(false)
    if (fileInput.current) fileInput.current.value = ''
  }

  const handleChoose = async (chosen: File | null) => {
    setFile(chosen)
    setReport(null)
    setResult(null)
    if (!chosen) return
    try {
      const res = await previewImport.mutateAsync(chosen)
      setReport(res.data)
    } catch (err) {
      toast({
        title: 'Could not read that workbook',
        description: explainError(err, 'Please check the file and try again.'),
        variant: 'destructive',
      })
      // The chosen file is kept deliberately. Clearing it here left the error sitting
      // next to "No file chosen", which reads as though nothing had been selected, and
      // forced the file to be picked again to retry.
      setReport(null)
    }
  }

  const handleCommit = async () => {
    if (!report) return
    try {
      const res = await commitImport.mutateAsync({ token: report.token, restoreDeleted })
      setResult(res.data)
      toast({
        title: 'Import complete',
        description: `${res.data.totals.created} added, ${res.data.totals.refreshed} updated.`,
      })
    } catch (err) {
      toast({
        title: 'The import did not finish',
        description: explainError(err, 'Nothing was duplicated — you can safely try again.'),
        variant: 'destructive',
      })
    }
  }

  const blockedTabs = report?.tabs.filter((t) => t.truncated) ?? []
  const nothingToDo =
    report !== null &&
    report.totals.toCreate === 0 &&
    report.totals.toRefresh === 0 &&
    report.totals.toRestore === 0

  return (
    <main className="space-y-4 p-4 md:p-6">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <FileSpreadsheet className="h-6 w-6" aria-hidden="true" />
          Import the training tracker
        </h1>
        <p className="max-w-3xl text-sm text-muted-foreground">
          Loads all six tabs of the Training Tracking workbook. Nothing is written until you confirm
          the summary, and re-importing is safe — rows are matched on what identifies them in the
          sheet, so a second import updates rather than duplicates.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">1. Choose the workbook</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <Label htmlFor="workbook" className="sr-only">
              Training Tracking workbook, as an .xlsx file
            </Label>
            <input
              ref={fileInput}
              id="workbook"
              type="file"
              accept=".xlsx,.xlsm"
              onChange={(e) => handleChoose(e.target.files?.[0] ?? null)}
              disabled={previewImport.isPending || commitImport.isPending}
              className="block text-sm file:mr-3 file:rounded-md file:border file:border-input file:bg-background file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-accent"
            />
            {/*
              Re-picking the same file fires no change event, so a failed read needs its
              own way back — otherwise the only route to a retry is choosing a different
              file and choosing this one again.
            */}
            {file && !report && !previewImport.isPending && (
              <Button variant="outline" size="sm" onClick={() => handleChoose(file)}>
                Try again
              </Button>
            )}
            {file && (
              <Button variant="outline" size="sm" onClick={reset}>
                Start over
              </Button>
            )}
          </div>
          <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            In Google Sheets: File → Download → Microsoft Excel (.xlsx). A CSV holds only one of the
            six tabs, and the other export formats silently cut the long tabs short and flatten line
            breaks inside cells.
          </p>
          {previewImport.isPending && (
            <p role="status" className="text-sm text-muted-foreground">
              Reading the workbook...
            </p>
          )}
        </CardContent>
      </Card>

      {report && !result && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">2. Check what will happen</CardTitle>
            <p className="text-sm text-muted-foreground">
              Tabs found: {report.sheetNames.join(', ')}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              {report.tabs.map((tab) => (
                <TabRow key={tab.key} tab={tab} />
              ))}
            </div>

            {blockedTabs.length > 0 && (
              <div
                className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm"
                role="alert"
              >
                <p className="font-medium">
                  {blockedTabs.length} tab{blockedTabs.length === 1 ? '' : 's'} will not be
                  imported.
                </p>
                <p className="mt-1 text-muted-foreground">
                  Each has fewer rows than it has ever had, which is what a truncated export looks
                  like. The rest of the file will still import. Re-download the workbook as .xlsx if
                  you expected those tabs.
                </p>
              </div>
            )}

            {report.totals.toRestore > 0 && (
              <div className="flex items-start gap-2 rounded-md border p-3">
                <Checkbox
                  id="restore-deleted"
                  checked={restoreDeleted}
                  onCheckedChange={(v) => setRestoreDeleted(v === true)}
                  className="mt-0.5"
                />
                <div className="text-sm">
                  <Label htmlFor="restore-deleted" className="font-medium">
                    Restore {report.totals.toRestore} row
                    {report.totals.toRestore === 1 ? '' : 's'} that were deleted in the CRM
                  </Label>
                  <p className="mt-1 text-muted-foreground">
                    These rows are in the workbook but somebody deleted them here. Left unticked
                    they stay deleted, and this import will report updating them while nothing
                    visibly changes.
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={handleCommit} disabled={commitImport.isPending || nothingToDo}>
                <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
                {commitImport.isPending ? 'Importing...' : 'Import now'}
              </Button>
              <span className="text-sm text-muted-foreground">
                {nothingToDo
                  ? 'Everything in this file is already imported and unchanged.'
                  : `${report.totals.toCreate} to add, ${report.totals.toRefresh} to update` +
                    (report.totals.toRestore > 0 && !restoreDeleted
                      ? `, ${report.totals.toRestore} left deleted`
                      : '')}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {result && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CheckCircle2 className="h-4 w-4 text-success" aria-hidden="true" />
              Imported
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <table className="w-full text-sm">
              <caption className="sr-only">What the import wrote, per tab</caption>
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th scope="col" className="py-1 font-medium">
                    Tab
                  </th>
                  <th scope="col" className="py-1 font-medium">
                    Added
                  </th>
                  <th scope="col" className="py-1 font-medium">
                    Updated
                  </th>
                </tr>
              </thead>
              <tbody>
                {result.tabs.map((t) => (
                  <tr key={t.key} className="border-b last:border-0">
                    <td className="py-1.5">{t.label}</td>
                    <td className="py-1.5 tabular-nums">{t.skipped ? '—' : t.created}</td>
                    <td className="py-1.5 tabular-nums">{t.skipped ? '—' : t.refreshed}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {result.totals.queuedNames > 0 && (
              <p className="text-sm text-muted-foreground">
                {result.totals.queuedNames} client name
                {result.totals.queuedNames === 1 ? '' : 's'} matched no CRM account and{' '}
                {result.totals.queuedNames === 1 ? 'is' : 'are'} queued on the Training screens for
                someone to decide. Nothing was guessed.
              </p>
            )}

            <Button variant="outline" size="sm" onClick={reset}>
              Import another file
            </Button>
          </CardContent>
        </Card>
      )}
    </main>
  )
}
